import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, competitionAPI, matchAPI, clubAPI, competitionTypeAPI } from '../../api/endpoints';
import { formatXPUNT, formatTimeAgo, formatDate } from '../../utils/helpers';
import { 
  FaUsers, FaTicketAlt, FaTrophy, FaHandshake,
  FaClock, FaCheck, FaTimes, FaEye,
  FaEdit, FaTrash, FaPlus, FaSearch,
  FaChartLine, FaCoins, FaBell, FaArrowLeft,
  FaShieldAlt, FaCrown, FaFootballBall,
  FaFlag, FaSave, FaUserFriends,
  FaChevronLeft, FaChevronRight, FaInfoCircle,
  FaPlay
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Loading state
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState({});
  const [pendingValidations, setPendingValidations] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [matches, setMatches] = useState([]);
  
  // UI states
  const [activeTab, setActiveTab] = useState('overview');
  
  // Pagination states
  const [teamPagination, setTeamPagination] = useState({ currentPage: 1, itemsPerPage: 10, total: 0 });
  const [leaguePagination, setLeaguePagination] = useState({ currentPage: 1, itemsPerPage: 10, total: 0 });
  const [competitionPagination, setCompetitionPagination] = useState({ currentPage: 1, itemsPerPage: 10, total: 0 });
  const [matchPagination, setMatchPagination] = useState({ currentPage: 1, itemsPerPage: 10, total: 0 });
  
  // Search states
  const [teamSearch, setTeamSearch] = useState('');
  const [leagueSearch, setLeagueSearch] = useState('');
  const [competitionSearch, setCompetitionSearch] = useState('');
  const [matchSearch, setMatchSearch] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Live Score Modal states
  const [showLiveScoreModal, setShowLiveScoreModal] = useState(false);
  const [liveScoreData, setLiveScoreData] = useState({ home_score: '', away_score: '' });
  
  // End Match Modal states
  const [showEndMatchModal, setShowEndMatchModal] = useState(false);
  const [endMatchData, setEndMatchData] = useState({ home_score: '', away_score: '', outcome: '' });

  // Admin check
  const isAdmin = user?.is_admin || 
                  user?.capabilities?.manage_options || 
                  user?.roles?.includes('administrator') ||
                  user?.roles?.includes('admin');

  // ==================== FETCH DATA ====================
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.allSettled([
          fetchAdminData(),
          fetchAllTeams(),
          fetchAllLeagues(),
          fetchAllCompetitions(),
          fetchAllMatches(),
        ]);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchAdminData = async () => {
    try {
      const [statsRes, pendingRes, activityRes] = await Promise.allSettled([
        adminAPI.getStats(),
        adminAPI.getPendingValidations(),
        adminAPI.getRecentActivity(),
      ]);
      
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data || {});
      if (pendingRes.status === 'fulfilled') setPendingValidations(pendingRes.value.data || []);
      if (activityRes.status === 'fulfilled') setRecentActivity(activityRes.value.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    }
  };

  const fetchAllTeams = async () => {
    try {
      const response = await clubAPI.getList({ per_page: 100 });
      const data = response.data.data || response.data || [];
      setTeams(data);
      setTeamPagination(prev => ({ ...prev, total: response.data.total || data.length }));
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      setTeams([]);
    }
  };

  const fetchAllLeagues = async () => {
    try {
      const response = await competitionTypeAPI.getList({ per_page: 100 });
      const data = response.data.data || response.data || [];
      setLeagues(data);
      setLeaguePagination(prev => ({ ...prev, total: response.data.total || data.length }));
    } catch (error) {
      console.error('Failed to fetch leagues:', error);
      setLeagues([]);
    }
  };

  const fetchAllCompetitions = async () => {
    try {
      const response = await competitionAPI.getList({ per_page: 100 });
      const data = response.data.data || response.data || [];
      setCompetitions(data);
      setCompetitionPagination(prev => ({ ...prev, total: response.data.total || data.length }));
    } catch (error) {
      console.error('Failed to fetch competitions:', error);
      setCompetitions([]);
    }
  };

  const fetchAllMatches = async () => {
    try {
      const response = await matchAPI.getList({ per_page: 100 });
      console.log('Fixtures response:', response.data);
      
      // Handle different response structures
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (response.data?.fixtures && Array.isArray(response.data.fixtures)) {
        data = response.data.fixtures;
      } else if (response.data?.matches && Array.isArray(response.data.matches)) {
        data = response.data.matches;
      }
      
      console.log('Parsed matches:', data);
      setMatches(data);
      setMatchPagination(prev => ({ 
        ...prev, 
        total: response.data?.total || data.length 
      }));
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      setMatches([]);
    }
  };

  // ==================== PAGINATION HELPERS ====================
  const paginateData = (data, currentPage, itemsPerPage) => {
    if (!Array.isArray(data)) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (total, itemsPerPage) => {
    return Math.ceil(total / itemsPerPage);
  };

  // ==================== FILTER FUNCTIONS ====================
  const filterTeams = (data, search) => {
    if (!search || !Array.isArray(data)) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter(team => 
      team.name?.toLowerCase().includes(lowerSearch) ||
      team.country?.toLowerCase().includes(lowerSearch)
    );
  };

  const filterLeagues = (data, search) => {
    if (!search || !Array.isArray(data)) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter(league => 
      league.name?.toLowerCase().includes(lowerSearch) ||
      league.country?.toLowerCase().includes(lowerSearch)
    );
  };

  const filterCompetitions = (data, search) => {
    if (!search || !Array.isArray(data)) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter(comp => 
      comp.title?.toLowerCase().includes(lowerSearch) ||
      comp.description?.toLowerCase().includes(lowerSearch)
    );
  };

  const filterMatches = (data, search) => {
    if (!search || !Array.isArray(data)) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter(match => {
      const homeTeam = 
        match.home_club_name || 
        match.home_team || 
        teams.find(t => t.id === match.home_club_id)?.name || 
        '';
      const awayTeam = 
        match.away_club_name || 
        match.away_team || 
        teams.find(t => t.id === match.away_club_id)?.name || 
        '';
      const league = 
        match.competition_name || 
        match.league || 
        leagues.find(l => l.id === match.competition_id)?.name || 
        '';
      return homeTeam.toLowerCase().includes(lowerSearch) || 
             awayTeam.toLowerCase().includes(lowerSearch) || 
             league.toLowerCase().includes(lowerSearch);
    });
  };

  const getPaginatedData = (data, search, pagination, filterFn) => {
    const safeData = Array.isArray(data) ? data : [];
    const filtered = filterFn(safeData, search);
    const total = filtered.length;
    const paginated = paginateData(filtered, pagination.currentPage, pagination.itemsPerPage);
    return { 
      data: paginated, 
      total, 
      totalPages: getTotalPages(total, pagination.itemsPerPage) 
    };
  };

  // ==================== MATCH STATUS ====================
  const getMatchStatus = (match) => {
    const kickoffTime = match.kickoff_time ? new Date(match.kickoff_time).getTime() : 0;
    const now = Date.now();
    const hasStarted = kickoffTime > 0 && kickoffTime <= now;
    
    const isEnded = match.is_ended === 1 || match.is_ended === true || match.status === 'ended';
    
    if (isEnded) {
      return { 
        label: 'Ended', 
        className: 'ended', 
        canUpdateScore: false, 
        canEnd: false 
      };
    }
    
    if (match.status === 'live' || hasStarted) {
      return { 
        label: 'Live', 
        className: 'live', 
        canUpdateScore: true, 
        canEnd: true 
      };
    }
    
    return { 
      label: 'Upcoming', 
      className: 'upcoming', 
      canUpdateScore: false, 
      canEnd: false 
    };
  };

  const calculateOutcome = (homeScore, awayScore) => {
    const home = parseInt(homeScore);
    const away = parseInt(awayScore);
    
    if (isNaN(home) || isNaN(away)) return '';
    if (home > away) return 'Home Win';
    if (away > home) return 'Away Win';
    return 'Draw';
  };

  // ==================== MODAL HANDLERS ====================
  const handleModalOpen = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    
    if (item) {
      setFormData(item);
    } else {
      setFormData({
        home_club_id: '',
        away_club_id: '',
        competition_id: '',
        venue: '',
        kickoff_time: '',
      });
    }
    
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // ==================== LIVE SCORE HANDLERS ====================
  const handleOpenLiveScore = (match) => {
    setEditingItem(match);
    setLiveScoreData({
      home_score: match.home_score !== undefined && match.home_score !== null ? match.home_score : '',
      away_score: match.away_score !== undefined && match.away_score !== null ? match.away_score : '',
    });
    setShowLiveScoreModal(true);
  };

  const handleCloseLiveScore = () => {
    setShowLiveScoreModal(false);
    setEditingItem(null);
    setLiveScoreData({ home_score: '', away_score: '' });
  };

  const handleUpdateLiveScore = async () => {
    if (liveScoreData.home_score === '' || liveScoreData.away_score === '') {
      toast.error('Please enter both scores');
      return;
    }

    const matchId = editingItem?.id;
    if (!matchId) {
      toast.error('No match selected');
      return;
    }

    try {
      const response = await matchAPI.updateLiveScore(matchId, {
        home_score: parseInt(liveScoreData.home_score),
        away_score: parseInt(liveScoreData.away_score),
      });
      
      if (response.data.success) {
        toast.success('Live score updated! Match is still live.');
        handleCloseLiveScore();
        await fetchAllMatches();
      } else {
        toast.error(response.data?.message || 'Failed to update live score');
      }
    } catch (error) {
      console.error('Live score update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update live score');
    }
  };

  // ==================== END MATCH HANDLERS ====================
  const handleOpenEndMatch = (match) => {
    setEditingItem(match);
    setEndMatchData({
      home_score: match.home_score !== undefined && match.home_score !== null ? match.home_score : '',
      away_score: match.away_score !== undefined && match.away_score !== null ? match.away_score : '',
      outcome: '',
    });
    setShowEndMatchModal(true);
  };

  const handleCloseEndMatch = () => {
    setShowEndMatchModal(false);
    setEditingItem(null);
    setEndMatchData({ home_score: '', away_score: '', outcome: '' });
  };

  const handleEndMatch = async () => {
    if (endMatchData.home_score === '' || endMatchData.away_score === '') {
      toast.error('Please enter both scores');
      return;
    }

    const matchId = editingItem?.id;
    if (!matchId) {
      toast.error('No match selected');
      return;
    }

    if (!window.confirm('End this match? This will process all predictions and H2H games.')) {
      return;
    }

    try {
      const response = await matchAPI.endMatch(matchId, {
        home_score: parseInt(endMatchData.home_score),
        away_score: parseInt(endMatchData.away_score),
      });
      
      if (response.data.success) {
        const processed = response.data.processed || {};
        const predCount = processed.predictions_updated || 0;
        const h2hCount = processed.h2h_updated || 0;
        
        let msg = '✅ Match ended!';
        if (predCount > 0 || h2hCount > 0) {
          const parts = [];
          if (predCount > 0) parts.push(`${predCount} prediction(s)`);
          if (h2hCount > 0) parts.push(`${h2hCount} H2H game(s)`);
          msg += ` Processed ${parts.join(' and ')}.`;
        }
        
        toast.success(msg, { duration: 5000 });
        handleCloseEndMatch();
        await fetchAllMatches();
      } else {
        toast.error(response.data?.message || 'Failed to end match');
      }
    } catch (error) {
      console.error('End match error:', error);
      toast.error(error.response?.data?.message || 'Failed to end match');
    }
  };

  // ==================== VALIDATION ====================
  const handleValidate = async (betId, status) => {
    try {
      await adminAPI.validateBet(betId, { status });
      toast.success(`Bet code ${status} successfully`);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to validate bet');
    }
  };

  // ==================== TEAM CRUD ====================
  const handleSaveTeam = async () => {
    try {
      if (editingItem) {
        await clubAPI.update(editingItem.id, formData);
        toast.success('Team updated successfully');
      } else {
        await clubAPI.create(formData);
        toast.success('Team created successfully');
      }
      setShowModal(false);
      await fetchAllTeams();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save team');
    }
  };

  // ==================== LEAGUE CRUD ====================
  const handleSaveLeague = async () => {
    try {
      if (editingItem) {
        await competitionTypeAPI.update(editingItem.id, formData);
        toast.success('League updated successfully');
      } else {
        await competitionTypeAPI.create(formData);
        toast.success('League created successfully');
      }
      setShowModal(false);
      await fetchAllLeagues();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save league');
    }
  };

  // ==================== COMPETITION CRUD ====================
  const handleSaveCompetition = async () => {
    try {
      if (editingItem) {
        await competitionAPI.update(editingItem.id, formData);
        toast.success('Competition updated successfully');
      } else {
        await competitionAPI.create(formData);
        toast.success('Competition created successfully');
      }
      setShowModal(false);
      await fetchAllCompetitions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save competition');
    }
  };

  // ==================== MATCH CRUD ====================
  const handleSaveMatch = async () => {
    if (formData.home_club_id === formData.away_club_id) {
      toast.error('Home and Away teams must be different');
      return;
    }

    if (!formData.home_club_id || !formData.away_club_id || !formData.competition_id || !formData.kickoff_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const matchData = {
        home_club_id: parseInt(formData.home_club_id),
        away_club_id: parseInt(formData.away_club_id),
        competition_id: parseInt(formData.competition_id),
        venue: formData.venue || '',
        kickoff_time: formData.kickoff_time,
      };

      if (editingItem) {
        await matchAPI.update(editingItem.id, matchData);
        toast.success('Match updated successfully');
      } else {
        await matchAPI.create(matchData);
        toast.success('Match created successfully');
      }
      setShowModal(false);
      await fetchAllMatches();
    } catch (error) {
      console.error('Save match error:', error);
      toast.error(error.response?.data?.message || 'Failed to save match');
    }
  };

  // ==================== DELETE ====================
  const handleDeleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      if (type === 'team') {
        await clubAPI.delete(id);
        await fetchAllTeams();
      } else if (type === 'league') {
        await competitionTypeAPI.delete(id);
        await fetchAllLeagues();
      } else if (type === 'competition') {
        await competitionAPI.delete(id);
        await fetchAllCompetitions();
      } else if (type === 'match') {
        await matchAPI.delete(id);
        await fetchAllMatches();
      }
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  // ==================== PAGINATION ====================
  const handlePageChange = (type, newPage) => {
    const setters = {
      teams: setTeamPagination,
      leagues: setLeaguePagination,
      competitions: setCompetitionPagination,
      matches: setMatchPagination
    };
    const setter = setters[type];
    if (setter) {
      setter(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-controls">
        <button 
          className="page-btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <FaChevronLeft />
        </button>
        {startPage > 1 && (
          <>
            <button className="page-btn" onClick={() => onPageChange(1)}>1</button>
            {startPage > 2 && <span className="page-ellipsis">...</span>}
          </>
        )}
        {pages.map(page => (
          <button 
            key={page}
            className={`page-btn ${page === currentPage ? 'active' : ''}`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="page-ellipsis">...</span>}
            <button className="page-btn" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
          </>
        )}
        <button 
          className="page-btn"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <FaChevronRight />
        </button>
      </div>
    );
  };

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <button onClick={() => navigate(-1)} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <div>
            <h1>
              <FaShieldAlt className="admin-icon" /> Admin Dashboard
            </h1>
            <p className="admin-subtitle">Manage your platform</p>
          </div>
        </div>
        <div className="admin-header-right">
          <span className="admin-badge">
            <FaCrown /> Admin
          </span>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="admin-stats">
        <div className="admin-stat-card">
          <FaUsers className="stat-icon users" />
          <div className="stat-info">
            <span className="stat-number">{stats.total_users || 0}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaTicketAlt className="stat-icon bets" />
          <div className="stat-info">
            <span className="stat-number">{stats.total_bet_codes || 0}</span>
            <span className="stat-label">Bet Codes</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaTrophy className="stat-icon comps" />
          <div className="stat-info">
            <span className="stat-number">{stats.total_competitions || 0}</span>
            <span className="stat-label">Competitions</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaHandshake className="stat-icon h2h" />
          <div className="stat-info">
            <span className="stat-number">{stats.total_h2h_games || 0}</span>
            <span className="stat-label">H2H Games</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaClock className="stat-icon pending" />
          <div className="stat-info">
            <span className="stat-number">{stats.pending_validations || 0}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <FaCoins className="stat-icon fees" />
          <div className="stat-info">
            <span className="stat-number">{formatXPUNT(stats.system_fees || 0)}</span>
            <span className="stat-label">Fees</span>
          </div>
        </div>
        <div className="admin-stat-card crypto-card">
          <Link to="/admin/crypto/deposits" className="crypto-link">
            <FaCoins className="stat-icon crypto" />
            <span>Crypto Deposits</span>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FaChartLine /> Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'validations' ? 'active' : ''}`}
          onClick={() => setActiveTab('validations')}
        >
          <FaCheck /> Validations
          {pendingValidations.length > 0 && (
            <span className="badge">{pendingValidations.length}</span>
          )}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveTab('teams')}
        >
          <FaUserFriends /> Teams
        </button>
        <button 
          className={`tab-btn ${activeTab === 'leagues' ? 'active' : ''}`}
          onClick={() => setActiveTab('leagues')}
        >
          <FaFlag /> Leagues
        </button>
        <button 
          className={`tab-btn ${activeTab === 'competitions' ? 'active' : ''}`}
          onClick={() => setActiveTab('competitions')}
        >
          <FaTrophy /> Competitions
        </button>
        <button 
          className={`tab-btn ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
        >
          <FaFootballBall /> Matches
        </button>
        <button 
          className={`tab-btn ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          <FaBell /> Activity
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-content">
        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="admin-card">
              <h3>Quick Actions</h3>
              <div className="quick-actions">
                <button className="quick-action" onClick={() => handleModalOpen('team')}>
                  <FaPlus /> Add Team
                </button>
                <button className="quick-action" onClick={() => handleModalOpen('league')}>
                  <FaPlus /> Add League
                </button>
                <button className="quick-action" onClick={() => handleModalOpen('competition')}>
                  <FaPlus /> Add Competition
                </button>
                <button className="quick-action" onClick={() => handleModalOpen('match')}>
                  <FaPlus /> Add Match
                </button>
              </div>
            </div>

            <div className="admin-card">
              <h3>System Overview</h3>
              <div className="system-info">
                <div className="info-item">
                  <span>Total Transactions</span>
                  <span>{stats.total_transactions || 0}</span>
                </div>
                <div className="info-item">
                  <span>Total Prize Pot</span>
                  <span>{formatXPUNT(stats.total_pot || 0)}</span>
                </div>
                <div className="info-item">
                  <span>Pending Validations</span>
                  <span className="highlight">{stats.pending_validations || 0}</span>
                </div>
                <div className="info-item">
                  <span>Total Teams</span>
                  <span>{teams.length}</span>
                </div>
                <div className="info-item">
                  <span>Total Leagues</span>
                  <span>{leagues.length}</span>
                </div>
                <div className="info-item">
                  <span>Total Matches</span>
                  <span>{matches.length}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== VALIDATIONS TAB ==================== */}
        {/* ==================== VALIDATIONS TAB ==================== */}
{activeTab === 'validations' && (
  <div className="validations-section">
    <div className="admin-card">
      <div className="card-header">
        <h3>
          <FaCheck /> Pending Bet Validations 
          {pendingValidations.length > 0 && (
            <span className="count-badge">{pendingValidations.length}</span>
          )}
        </h3>
      </div>

      {pendingValidations.length === 0 ? (
        <div className="empty-state">
          <FaCheck className="empty-icon" />
          <p>No pending validations 🎉</p>
          <small>All bet codes have been validated</small>
        </div>
      ) : (
        <div className="validation-list">
          {pendingValidations.map((bet) => {
            // Normalize the marked result for display
            const markedResult = (bet.marked_result || '').toLowerCase();
            const isWon = markedResult === 'won' || markedResult === 'win';
            const isLost = markedResult === 'loss' || markedResult === 'lost' || markedResult === 'loose';
            
            return (
              <div key={bet.id} className="validation-item">
                {/* Validation Header */}
                <div className="validation-header-row">
                  <div className="validation-author">
                    {bet.author_avatar && (
                      <img 
                        src={bet.author_avatar} 
                        alt={bet.author} 
                        className="author-avatar-small"
                      />
                    )}
                    <div>
                      <strong>{bet.author}</strong>
                      <span className="validation-time">
                        {formatTimeAgo(bet.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Result Badge */}
                  <div className={`validation-result-badge ${
                    isWon ? 'won' : isLost ? 'lost' : 'pending'
                  }`}>
                    {isWon && <><FaCheck /> User Marked WON</>}
                    {isLost && <><FaTimes /> User Marked LOST</>}
                    {!isWon && !isLost && <><FaClock /> Awaiting Mark</>}
                  </div>
                </div>

                {/* Bet Details Grid */}
                <div className="validation-details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Platform</span>
                    <span className="detail-value platform-badge">
                      {bet.platform || 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Bet Code</span>
                    <span className="detail-value code-highlight">
                      {bet.code || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Games</span>
                    <span className="detail-value">{bet.number_of_games || 0}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Market</span>
                    <span className="detail-value">{bet.market_type || 'Multiple'}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Odds</span>
                    <span className="detail-value gold">{bet.odds || 0}</span>
                  </div>
                  
                  {bet.stake > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Stake</span>
                      <span className="detail-value">{formatXPUNT(bet.stake)}</span>
                    </div>
                  )}
                  
                  {bet.potential_win > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Potential Win</span>
                      <span className="detail-value gold">{formatXPUNT(bet.potential_win)}</span>
                    </div>
                  )}
                  
                  {bet.confidence && (
                    <div className="detail-item">
                      <span className="detail-label">Confidence</span>
                      <span className={`detail-value confidence-${bet.confidence}`}>
                        {bet.confidence}
                      </span>
                    </div>
                  )}
                </div>

                {/* Title */}
                {bet.title && (
                  <div className="validation-bet-title">
                    <strong>{bet.title}</strong>
                  </div>
                )}

                {/* Badges */}
                <div className="validation-badges">
                  {bet.is_private && (
                    <span className="badge-tag private">🔒 Private</span>
                  )}
                  {bet.is_rollover && (
                    <span className="badge-tag rollover">
                      🔄 Rollover #{bet.rollover_order}
                    </span>
                  )}
                  {bet.marks_won > 0 && (
                    <span className="badge-tag won">✅ {bet.marks_won} Won</span>
                  )}
                  {bet.marks_loss > 0 && (
                    <span className="badge-tag lost">❌ {bet.marks_loss} Lost</span>
                  )}
                </div>

                {/* Proof Image */}
                {bet.proof_image && (
                  <div className="proof-section">
                    <div className="proof-header">
                      <strong>📸 Proof Image</strong>
                      {bet.proof_uploaded_at && (
                        <span className="proof-time">
                          Uploaded {formatTimeAgo(bet.proof_uploaded_at)}
                        </span>
                      )}
                    </div>
                    <div className="proof-image-wrapper">
                      <img 
                        src={bet.proof_image} 
                        alt="Proof" 
                        onClick={() => window.open(bet.proof_image, '_blank')}
                      />
                    </div>
                  </div>
                )}

                {/* Bet Image (if different from proof) */}
                {bet.image_url && bet.image_url !== bet.proof_image && (
                  <div className="bet-image-section">
                    <strong>📷 Bet Image</strong>
                    <img 
                      src={bet.image_url} 
                      alt="Bet" 
                      className="bet-image-preview"
                      onClick={() => window.open(bet.image_url, '_blank')}
                    />
                  </div>
                )}

                {/* View Bet Button */}
                <Link 
                  to={`/bet-codes/${bet.id}`} 
                  className="view-bet-link"
                  target="_blank"
                >
                  <FaEye /> View Full Bet Details
                </Link>

                {/* Validation Actions */}
                <div className="validation-actions">
                  <button 
                    className="btn btn-success btn-sm"
                    onClick={() => handleValidate(bet.id, 'approved')}
                  >
                    <FaCheck /> Approve
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => handleValidate(bet.id, 'rejected')}
                  >
                    <FaTimes /> Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
)}

        {/* ==================== TEAMS TAB ==================== */}
        {activeTab === 'teams' && (
          <div className="admin-card">
            <div className="card-header">
              <h3><FaUserFriends /> Teams ({teams.length})</h3>
              <button className="btn btn-primary btn-sm" onClick={() => handleModalOpen('team')}>
                <FaPlus /> Add Team
              </button>
            </div>
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search teams..."
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {(() => {
              const { data: paginatedTeams, total, totalPages } = getPaginatedData(
                teams, teamSearch, teamPagination, filterTeams
              );
              
              if (paginatedTeams.length === 0) {
                return (
                  <div className="empty-state">
                    <p>No teams found</p>
                  </div>
                );
              }
              
              return (
                <>
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Team Name</th>
                          <th>Country</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTeams.map((team) => (
                          <tr key={team.id}>
                            <td>
                              <div className="team-cell">
                                {team.logo && (
                                  <img src={team.logo} alt={team.name} className="team-logo-small" />
                                )}
                                <strong>{team.name}</strong>
                              </div>
                            </td>
                            <td>{team.country || 'N/A'}</td>
                            <td>{formatDate(team.created_at)}</td>
                            <td>
                              <div className="action-buttons-group">
                                <button 
                                  className="action-btn edit"
                                  onClick={() => handleModalOpen('team', team)}
                                >
                                  <FaEdit />
                                </button>
                                <button 
                                  className="action-btn delete"
                                  onClick={() => handleDeleteItem('team', team.id)}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-footer">
                    <span className="table-info">
                      Showing {paginatedTeams.length} of {total} teams
                    </span>
                    <PaginationControls 
                      currentPage={teamPagination.currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => handlePageChange('teams', page)}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ==================== LEAGUES TAB ==================== */}
        {activeTab === 'leagues' && (
          <div className="admin-card">
            <div className="card-header">
              <h3><FaFlag /> Leagues ({leagues.length})</h3>
              <button className="btn btn-primary btn-sm" onClick={() => handleModalOpen('league')}>
                <FaPlus /> Add League
              </button>
            </div>
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search leagues..."
                value={leagueSearch}
                onChange={(e) => setLeagueSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {(() => {
              const { data: paginatedLeagues, total, totalPages } = getPaginatedData(
                leagues, leagueSearch, leaguePagination, filterLeagues
              );
              
              if (paginatedLeagues.length === 0) {
                return (
                  <div className="empty-state">
                    <p>No leagues found</p>
                  </div>
                );
              }
              
              return (
                <>
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Country</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedLeagues.map((league) => (
                          <tr key={league.id}>
                            <td><strong>{league.name}</strong></td>
                            <td>{league.country || 'Global'}</td>
                            <td>{formatDate(league.created_at)}</td>
                            <td>
                              <div className="action-buttons-group">
                                <button 
                                  className="action-btn edit"
                                  onClick={() => handleModalOpen('league', league)}
                                >
                                  <FaEdit />
                                </button>
                                <button 
                                  className="action-btn delete"
                                  onClick={() => handleDeleteItem('league', league.id)}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-footer">
                    <span className="table-info">
                      Showing {paginatedLeagues.length} of {total} leagues
                    </span>
                    <PaginationControls 
                      currentPage={leaguePagination.currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => handlePageChange('leagues', page)}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ==================== COMPETITIONS TAB ==================== */}
        {activeTab === 'competitions' && (
          <div className="admin-card">
            <div className="card-header">
              <h3><FaTrophy /> Competitions ({competitions.length})</h3>
              <button className="btn btn-primary btn-sm" onClick={() => handleModalOpen('competition')}>
                <FaPlus /> Add Competition
              </button>
            </div>
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search competitions..."
                value={competitionSearch}
                onChange={(e) => setCompetitionSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {(() => {
              const { data: paginatedCompetitions, total, totalPages } = getPaginatedData(
                competitions, competitionSearch, competitionPagination, filterCompetitions
              );
              
              if (paginatedCompetitions.length === 0) {
                return (
                  <div className="empty-state">
                    <p>No competitions found</p>
                  </div>
                );
              }
              
              return (
                <>
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Status</th>
                          <th>Players</th>
                          <th>Prize Pool</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCompetitions.map((comp) => (
                          <tr key={comp.id}>
                            <td><strong>{comp.title}</strong></td>
                            <td>
                              <span className={`status-badge ${comp.status}`}>
                                {comp.status}
                              </span>
                            </td>
                            <td>{comp.players_count || 0}</td>
                            <td>{formatXPUNT(comp.total_prize_pool || 0)}</td>
                            <td>{formatDate(comp.created_at)}</td>
                            <td>
                              <div className="action-buttons-group">
                                <button 
                                  className="action-btn edit"
                                  onClick={() => handleModalOpen('competition', comp)}
                                >
                                  <FaEdit />
                                </button>
                                <button 
                                  className="action-btn delete"
                                  onClick={() => handleDeleteItem('competition', comp.id)}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-footer">
                    <span className="table-info">
                      Showing {paginatedCompetitions.length} of {total} competitions
                    </span>
                    <PaginationControls 
                      currentPage={competitionPagination.currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => handlePageChange('competitions', page)}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ==================== MATCHES TAB ==================== */}
        {activeTab === 'matches' && (
          <div className="admin-card">
            <div className="card-header">
              <h3><FaFootballBall /> Matches ({matches.length})</h3>
              <button className="btn btn-primary btn-sm" onClick={() => handleModalOpen('match')}>
                <FaPlus /> Add Match
              </button>
            </div>
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search matches..."
                value={matchSearch}
                onChange={(e) => setMatchSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {(() => {
              const { data: paginatedMatches, total, totalPages } = getPaginatedData(
                matches, matchSearch, matchPagination, filterMatches
              );
              
              if (paginatedMatches.length === 0) {
                return (
                  <div className="empty-state">
                    <FaFootballBall className="empty-icon" />
                    <p>No matches found</p>
                    <small>Click "Add Match" to create your first match</small>
                  </div>
                );
              }
              
              return (
                <>
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Home</th>
                          <th>Away</th>
                          <th>League</th>
                          <th>Kickoff</th>
                          <th>Score</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedMatches.map((match) => {
                          const homeTeam = 
                            match.home_club_name || 
                            match.home_team || 
                            teams.find(t => t.id === match.home_club_id)?.name || 
                            'Unknown';
                            
                          const awayTeam = 
                            match.away_club_name || 
                            match.away_team || 
                            teams.find(t => t.id === match.away_club_id)?.name || 
                            'Unknown';
                            
                          const league = 
                            match.competition_name || 
                            match.league || 
                            leagues.find(l => l.id === match.competition_id)?.name || 
                            'N/A';
                          
                          const statusInfo = getMatchStatus(match);
                          const isEnded = statusInfo.className === 'ended';
                          const isLive = statusInfo.className === 'live';
                          const hasScore = match.home_score !== null && match.home_score !== undefined;
                          
                          return (
                            <tr key={match.id} className={isLive ? 'live-row' : ''}>
                              <td>
                                <div className="team-cell">
                                  {match.home_club_logo && (
                                    <img src={match.home_club_logo} alt={homeTeam} className="team-logo-small" />
                                  )}
                                  <strong>{homeTeam}</strong>
                                </div>
                              </td>
                              <td>
                                <div className="team-cell">
                                  {match.away_club_logo && (
                                    <img src={match.away_club_logo} alt={awayTeam} className="team-logo-small" />
                                  )}
                                  <strong>{awayTeam}</strong>
                                </div>
                              </td>
                              <td>{league}</td>
                              <td>{formatDate(match.kickoff_time)}</td>
                              <td>
                                {hasScore ? (
                                  <span className={isLive && !isEnded ? 'live-score' : 'result-score'}>
                                    {match.home_score} - {match.away_score}
                                    {isLive && !isEnded && (
                                      <span className="live-indicator-inline">LIVE</span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                <span className={`status-badge ${statusInfo.className}`}>
                                  {isLive && <span className="live-dot"></span>}
                                  {statusInfo.label}
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons-group">
                                  {/* Edit Match */}
                                  <button 
                                    className="action-btn edit"
                                    onClick={() => handleModalOpen('match', match)}
                                    title="Edit Match"
                                  >
                                    <FaEdit />
                                  </button>
                                  
                                  {/* Update Live Score - Only when live */}
                                  {statusInfo.canUpdateScore && (
                                    <button 
                                      className="action-btn live-score"
                                      onClick={() => handleOpenLiveScore(match)}
                                      title="Update Score (Match stays live)"
                                    >
                                      <FaClock />
                                    </button>
                                  )}
                                  
                                  {/* End Match - Only when live */}
                                  {statusInfo.canEnd && (
                                    <button 
                                      className="action-btn end-match"
                                      onClick={() => handleOpenEndMatch(match)}
                                      title="End Match & Process Results"
                                    >
                                      <FaCheck />
                                    </button>
                                  )}
                                  
                                  {/* Delete - Only for upcoming matches */}
                                  {!isLive && !isEnded && (
                                    <button 
                                      className="action-btn delete"
                                      onClick={() => handleDeleteItem('match', match.id)}
                                      title="Delete Match"
                                    >
                                      <FaTrash />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-footer">
                    <span className="table-info">
                      Showing {paginatedMatches.length} of {total} matches
                    </span>
                    <PaginationControls 
                      currentPage={matchPagination.currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => handlePageChange('matches', page)}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ==================== ACTIVITY TAB ==================== */}
        {activeTab === 'activity' && (
          <div className="activity-section">
            <div className="admin-card">
              <h3>Recent Activity</h3>
              {recentActivity.length === 0 ? (
                <div className="empty-state">
                  <p>No recent activity</p>
                </div>
              ) : (
                <div className="activity-list">
                  {recentActivity.map((item, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-icon" style={{ color: item.color }}>
                        <i className={item.icon}></i>
                      </div>
                      <div className="activity-content">
                        <p>{item.text}</p>
                        <span className="activity-time">{item.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ==================== MAIN MODAL (Team/League/Competition/Match) ==================== */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                {modalType === 'team' && (editingItem ? 'Edit Team' : 'Add Team')}
                {modalType === 'league' && (editingItem ? 'Edit League' : 'Add League')}
                {modalType === 'competition' && (editingItem ? 'Edit Competition' : 'Add Competition')}
                {modalType === 'match' && (editingItem ? 'Edit Match' : 'Add Match')}
              </h2>
              <button className="close-btn" onClick={handleModalClose}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              {/* Team Form */}
              {modalType === 'team' && (
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <label>Team Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleFormChange}
                      placeholder="e.g., Arsenal FC"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country || ''}
                      onChange={handleFormChange}
                      placeholder="e.g., England"
                    />
                  </div>
                  <div className="form-group">
                    <label>Logo URL</label>
                    <input
                      type="url"
                      name="logo"
                      value={formData.logo || ''}
                      onChange={handleFormChange}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </form>
              )}

              {/* League Form */}
              {modalType === 'league' && (
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <label>League Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleFormChange}
                      placeholder="e.g., Premier League"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country || ''}
                      onChange={handleFormChange}
                      placeholder="e.g., England"
                    />
                  </div>
                  <div className="form-group">
                    <label>Logo URL</label>
                    <input
                      type="url"
                      name="logo"
                      value={formData.logo || ''}
                      onChange={handleFormChange}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </form>
              )}

              {/* Competition Form */}
              {modalType === 'competition' && (
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title || ''}
                      onChange={handleFormChange}
                      placeholder="e.g., Premier League Predictions"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={formData.description || ''}
                      onChange={handleFormChange}
                      placeholder="Describe the competition..."
                      rows={3}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date *</label>
                      <input
                        type="datetime-local"
                        name="start_date"
                        value={formData.start_date || ''}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date *</label>
                      <input
                        type="datetime-local"
                        name="end_date"
                        value={formData.end_date || ''}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Visibility</label>
                    <select
                      name="visibility"
                      value={formData.visibility || 'public'}
                      onChange={handleFormChange}
                    >
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                    </select>
                  </div>
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_free"
                        checked={formData.is_free !== false}
                        onChange={handleFormChange}
                      />
                      Free Entry
                    </label>
                  </div>
                  {formData.is_free === false && (
                    <div className="form-group">
                      <label>Entry Price ($XPUNT)</label>
                      <input
                        type="number"
                        name="join_price"
                        value={formData.join_price || 0}
                        onChange={handleFormChange}
                        step="0.01"
                        min="0.01"
                      />
                    </div>
                  )}
                </form>
              )}

              {/* Match Form */}
              {modalType === 'match' && (
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <label>Home Team *</label>
                    <select
                      name="home_club_id"
                      value={formData.home_club_id || ''}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Home Team</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Away Team *</label>
                    <select
                      name="away_club_id"
                      value={formData.away_club_id || ''}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Away Team</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>League *</label>
                    <select
                      name="competition_id"
                      value={formData.competition_id || ''}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select League</option>
                      {leagues.map((league) => (
                        <option key={league.id} value={league.id}>
                          {league.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Venue</label>
                    <input
                      type="text"
                      name="venue"
                      value={formData.venue || ''}
                      onChange={handleFormChange}
                      placeholder="e.g., Emirates Stadium"
                    />
                  </div>
                  <div className="form-group">
                    <label>Kickoff Time *</label>
                    <input
                      type="datetime-local"
                      name="kickoff_time"
                      value={formData.kickoff_time || ''}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </form>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleModalClose}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  if (modalType === 'team') handleSaveTeam();
                  else if (modalType === 'league') handleSaveLeague();
                  else if (modalType === 'competition') handleSaveCompetition();
                  else if (modalType === 'match') handleSaveMatch();
                }}
              >
                <FaSave /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== LIVE SCORE MODAL ==================== */}
      {showLiveScoreModal && editingItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                <FaClock /> Update Live Score
              </h2>
              <button className="close-btn" onClick={handleCloseLiveScore}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="match-result-info">
                <h4>
                  {editingItem?.home_club_name || 
                    editingItem?.home_team || 
                    teams.find(t => t.id === editingItem?.home_club_id)?.name || 
                    'Home'} 
                  vs 
                  {editingItem?.away_club_name || 
                    editingItem?.away_team || 
                    teams.find(t => t.id === editingItem?.away_club_id)?.name || 
                    'Away'}
                </h4>
                <p className="match-league">
                  {editingItem?.competition_name || 
                    editingItem?.league || 
                    leagues.find(l => l.id === editingItem?.competition_id)?.name || 
                    'N/A'}
                </p>
                <span className="live-badge-modal">
                  <span className="live-dot"></span> LIVE
                </span>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Home Score</label>
                  <input
                    type="number"
                    value={liveScoreData.home_score}
                    onChange={(e) => setLiveScoreData(prev => ({ ...prev, home_score: e.target.value }))}
                    min="0"
                    placeholder="0"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Away Score</label>
                  <input
                    type="number"
                    value={liveScoreData.away_score}
                    onChange={(e) => setLiveScoreData(prev => ({ ...prev, away_score: e.target.value }))}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-info-box">
                <FaInfoCircle className="info-icon" />
                <div>
                  <strong>Live Score Update:</strong>
                  <ul className="info-list">
                    <li>✅ Updates the displayed score</li>
                    <li>✅ Match remains <strong>LIVE</strong></li>
                    <li>✅ You can update score multiple times</li>
                    <li>⏸️ No predictions/H2H are calculated yet</li>
                    <li>⏸️ Match is not ended</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseLiveScore}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdateLiveScore}>
                <FaSave /> Update Score
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== END MATCH MODAL ==================== */}
      {showEndMatchModal && editingItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                <FaCheck /> End Match
              </h2>
              <button className="close-btn" onClick={handleCloseEndMatch}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="match-result-info">
                <h4>
                  {editingItem?.home_club_name || 
                    editingItem?.home_team || 
                    teams.find(t => t.id === editingItem?.home_club_id)?.name || 
                    'Home'} 
                  vs 
                  {editingItem?.away_club_name || 
                    editingItem?.away_team || 
                    teams.find(t => t.id === editingItem?.away_club_id)?.name || 
                    'Away'}
                </h4>
                <p className="match-league">
                  {editingItem?.competition_name || 
                    editingItem?.league || 
                    leagues.find(l => l.id === editingItem?.competition_id)?.name || 
                    'N/A'}
                </p>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Final Home Score *</label>
                  <input
                    type="number"
                    value={endMatchData.home_score}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEndMatchData(prev => {
                        const newData = { ...prev, home_score: val };
                        if (newData.home_score !== '' && newData.away_score !== '') {
                          newData.outcome = calculateOutcome(newData.home_score, newData.away_score);
                        }
                        return newData;
                      });
                    }}
                    min="0"
                    placeholder="0"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Final Away Score *</label>
                  <input
                    type="number"
                    value={endMatchData.away_score}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEndMatchData(prev => {
                        const newData = { ...prev, away_score: val };
                        if (newData.home_score !== '' && newData.away_score !== '') {
                          newData.outcome = calculateOutcome(newData.home_score, newData.away_score);
                        }
                        return newData;
                      });
                    }}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>

              {endMatchData.home_score !== '' && endMatchData.away_score !== '' && (
                <div className="auto-outcome-display">
                  <span className="auto-outcome-label">Final outcome:</span>
                  <span className="auto-outcome-value">
                    {calculateOutcome(endMatchData.home_score, endMatchData.away_score)}
                  </span>
                </div>
              )}

              <div className="form-info-box warning">
                <FaInfoCircle className="info-icon" />
                <div>
                  <strong>⚠️ Ending this match will:</strong>
                  <ul className="info-list">
                    <li>✅ Set final score: <strong>{endMatchData.home_score || '0'} - {endMatchData.away_score || '0'}</strong></li>
                    <li>✅ Change status to <strong>ENDED</strong></li>
                    <li>🔄 Process ALL predictions linked to this match</li>
                    <li>🔄 Calculate points for every player</li>
                    <li>🔄 Determine prediction winners</li>
                    <li>🔄 Distribute prediction prizes</li>
                    <li>🔄 Process ALL H2H games</li>
                    <li>🔄 Distribute H2H winnings</li>
                  </ul>
                  <p className="warning-text">
                    ⚠️ This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseEndMatch}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={handleEndMatch}>
                <FaCheck /> End Match & Process
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;